import { atom, useAtom } from 'jotai';
import type { ThemeMode } from '../types/theme.types';
import { useMemo, useEffect } from 'react';
import { generateThemeTokens } from '../utils/theme.util';
import { useAppStateStorage } from '../hooks/use-local-storage.hook';
import {
  defaultPrimaryColor,
  defaultSecondaryColor,
  defaultAccentColor,
} from '../const/default-branding-colors.const';

// Get initial theme from local storage or default to 'light'
const getInitialTheme = (): ThemeMode => {
  const savedState = useAppStateStorage.get();
  return savedState.theme || 'light';
};

// Global theme atoms
export const themeAtom = atom<ThemeMode>(getInitialTheme());
export const primaryColorAtom = atom<string>(defaultPrimaryColor);
export const secondaryColorAtom = atom<string>(defaultSecondaryColor);
export const accentColorAtom = atom<string>(defaultAccentColor);

// Derived atom for theme tokens that automatically updates when colors or theme change
export const themeTokensAtom = atom((get) => {
  const theme = get(themeAtom);
  const primaryColor = get(primaryColorAtom);
  const secondaryColor = get(secondaryColorAtom);
  const accentColor = get(accentColorAtom);

  return generateThemeTokens(primaryColor, secondaryColor, accentColor, theme);
});

// Custom hook for theme mode management
export const useTheme = () => {
  const [theme, setTheme] = useAtom(themeAtom);

  // Initialize theme from local storage on mount
  useEffect(() => {
    const savedState = useAppStateStorage.get();
    if (savedState.theme && savedState.theme !== theme) {
      setTheme(savedState.theme);
    }
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    useAppStateStorage.setTheme(newTheme);
  };

  return { theme, setTheme, toggleTheme };
};

// Custom hook for branding colors management
export const useBrandingColors = () => {
  const [primaryColor, setPrimaryColor] = useAtom(primaryColorAtom);
  const [secondaryColor, setSecondaryColor] = useAtom(secondaryColorAtom);
  const [accentColor, setAccentColor] = useAtom(accentColorAtom);

  const resetToDefaults = () => {
    setPrimaryColor(defaultPrimaryColor);
    setSecondaryColor(defaultSecondaryColor);
    setAccentColor(defaultAccentColor);
  };

  const setBrandingColors = (colors: { primary?: string; secondary?: string; accent?: string }) => {
    if (colors.primary) setPrimaryColor(colors.primary);
    if (colors.secondary) setSecondaryColor(colors.secondary);
    if (colors.accent) setAccentColor(colors.accent);
  };

  return {
    primaryColor,
    secondaryColor,
    accentColor,
    setPrimaryColor,
    setSecondaryColor,
    setAccentColor,
    setBrandingColors,
    resetToDefaults,
  };
};

// Custom hook for getting theme tokens (reactive to all theme changes)
export const useThemeTokens = () => {
  const [tokens] = useAtom(themeTokensAtom);
  return tokens;
};

// Legacy hook for backward compatibility (simplified)
export const useBranding = ({
  primaryColorInput,
  secondaryColorInput,
  accentColorInput,
  theme,
}: {
  primaryColorInput?: string;
  secondaryColorInput?: string;
  accentColorInput?: string;
  theme?: 'light' | 'dark';
} = {}) => {
  const {
    primaryColor,
    secondaryColor,
    accentColor,
    setPrimaryColor,
    setSecondaryColor,
    setAccentColor,
  } = useBrandingColors();

  const [currentTheme] = useAtom(themeAtom);

  // Use provided colors or fall back to global state
  const effectivePrimaryColor = primaryColorInput || primaryColor;
  const effectiveSecondaryColor = secondaryColorInput || secondaryColor;
  const effectiveAccentColor = accentColorInput || accentColor;
  const effectiveTheme = theme || currentTheme;

  const tokens = useMemo(() => {
    return generateThemeTokens(
      effectivePrimaryColor,
      effectiveSecondaryColor,
      effectiveAccentColor,
      effectiveTheme
    );
  }, [effectivePrimaryColor, effectiveSecondaryColor, effectiveAccentColor, effectiveTheme]);

  return {
    tokens,
    primaryColor: effectivePrimaryColor,
    secondaryColor: effectiveSecondaryColor,
    accentColor: effectiveAccentColor,
    setPrimaryColor,
    setSecondaryColor,
    setAccentColor,
  };
};
